/**
 * Sport category icons for sidebar, sliders, and grouped odds panels.
 *
 * Sport *names* come from the backend (`/get-Gamedata` groups) or odds API
 * (`sport_title` on each event). Icons are local static SVGs only — the API
 * does not provide logos for sport categories.
 */

const DEFAULT_SPORT_ICON = "aTozIcon";

/** Exact match on normalized sport name / group label */
const EXACT_SPORT_ICON = {
  "a-z betting": "aTozIcon",
  baseball: "ballIcon",
  basketball: "tennesBallIcon",
  boxing: "boxing",
  cricket: "cricatIcon",
  tennis: "tennesBallIcon",
  golf: "ballIcon",
  handball: "whiteFootbalIcon",
  "mixed martial arts": "boxing",
  mma: "boxing",
  "rugby league": "foodBallIcon",
  "rugby union": "foodBallIcon",
  rugby: "foodBallIcon",
  "aussie rules": "foodBallIcon",
  afl: "foodBallIcon",
  "american football": "foodBallIcon",
  nfl: "foodBallIcon",
  ncaaf: "foodBallIcon",
  cfl: "foodBallIcon",
  soccer: "whiteFootbalIcon",
  football: "whiteFootbalIcon",
  "ice hockey": "whiteBatIcon",
  nhl: "whiteBatIcon",
  hockey: "whiteBatIcon",
  volleyball: "whiteFootbalIcon",
  darts: "whiteBatIcon",
  politics: "aTozIcon",
  "horse racing": "aTozIcon",
};

/**
 * Ordered rules — first match wins. More specific patterns must come first.
 * Uses word boundaries where needed so "cfl" does not match "afl".
 */
const SPORT_ICON_RULES = [
  { test: (k) => /mixed martial arts|\b(mma|ufc)\b/.test(k), icon: "boxing" },
  { test: (k) => /rugby/.test(k), icon: "foodBallIcon" },
  { test: (k) => /aussie rules|\bafl\b/.test(k), icon: "foodBallIcon" },
  {
    test: (k) =>
      /american football|canadian football|\b(nfl|ncaaf|cfl)\b|gridiron/.test(k),
    icon: "foodBallIcon",
  },
  { test: (k) => /basketball|\b(nba|wnba)\b/.test(k), icon: "tennesBallIcon" },
  { test: (k) => /baseball|\bmlb\b/.test(k), icon: "ballIcon" },
  { test: (k) => /handball/.test(k), icon: "whiteFootbalIcon" },
  { test: (k) => /\bgolf\b/.test(k), icon: "ballIcon" },
  { test: (k) => /boxing/.test(k), icon: "boxing" },
  { test: (k) => /cricket/.test(k), icon: "cricatIcon" },
  { test: (k) => /tennis/.test(k), icon: "tennesBallIcon" },
  {
    test: (k) =>
      /soccer|\bmls\b|premier league|la liga|serie a|bundesliga|uefa|fifa/.test(k),
    icon: "whiteFootbalIcon",
  },
  { test: (k) => /\bhockey\b|nhl/.test(k), icon: "whiteBatIcon" },
  {
    test: (k) => /\bfootball\b/.test(k) && !/american|canadian|aussie|rugby/.test(k),
    icon: "whiteFootbalIcon",
  },
];

export const normalizeSportKey = (sportName) =>
  String(sportName ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

export const getSportIcon = (sportName) => {
  const key = normalizeSportKey(sportName);
  if (!key) return DEFAULT_SPORT_ICON;

  if (EXACT_SPORT_ICON[key]) {
    return EXACT_SPORT_ICON[key];
  }

  const rule = SPORT_ICON_RULES.find(({ test }) => test(key));
  return rule ? rule.icon : DEFAULT_SPORT_ICON;
};
